import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '@/lib/supabase';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import type { Organization, Lang } from '@/lib/partnerTypes';

/* ─── Types ─────────────────────────────────────────────────────────── */
type ViewMode = 'grid' | 'list';
type SortField = 'name' | 'region' | 'status' | 'date' | 'custom';
type SortDir = 'asc' | 'desc';

/* ─── Header ─────────────────────────────────────────────────────────── */
function PortalHeader({ role, fullName, lang, setLang, onLogout }: {
  role: string; fullName: string | null; lang: Lang; setLang: (l: Lang) => void; onLogout: () => void;
}) {
  const roleLabel: Record<string, string> = { admin: 'Administrator', manager: 'Manager', partner: 'Partner' };
  return (
    <header className="bg-[#1a2e4a] text-white px-6 md:px-10 py-3 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity cursor-pointer">
          <i className="ri-arrow-left-line text-sm" />
          <span className="text-xs hidden sm:block">Main Site</span>
        </Link>
        <div className="w-px h-5 bg-white/20" />
        <span className="text-sm font-semibold tracking-wide">Partner Portal — NGO Catalog</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
          <i className="ri-user-line" />
          <span>{fullName || 'User'}</span>
          <span className="bg-white/10 px-2 py-0.5 rounded-full">{roleLabel[role] || role}</span>
        </div>
        <div className="flex items-center bg-white/10 rounded-lg p-0.5">
          {(['en', 'ro'] as Lang[]).map(l => (
            <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${lang === l ? 'bg-white text-[#1a2e4a]' : 'text-white/70 hover:text-white'}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-logout-box-line" /><span className="hidden sm:block">Sign out</span>
        </button>
      </div>
    </header>
  );
}

/* ─── Card (Grid) ────────────────────────────────────────────────────── */
function OrgCardContent({ org, lang, role, isDragging }: { org: Organization; lang: Lang; role: string; isDragging?: boolean }) {
  const name = lang === 'en' ? org.name_en : org.name_ro;
  return (
    <div className={`bg-white border rounded-xl p-5 transition-all group h-full flex flex-col ${isDragging ? 'border-[#1a2e4a]/40 opacity-50' : 'border-stone-200 hover:border-[#1a2e4a]/30'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#1a2e4a]/8 rounded-lg flex-shrink-0">
          {org.logo_url ? <img src={org.logo_url} alt="" className="w-8 h-8 object-contain" /> : <i className="ri-building-line text-[#1a2e4a] text-lg" />}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${org.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'}`}>
          {org.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      </div>
      <h3 className="text-sm font-bold text-[#1a2e4a] leading-snug mb-1 group-hover:text-[#253f62]">{name}</h3>
      {org.region_city && <p className="text-xs text-stone-400 flex items-center gap-1"><i className="ri-map-pin-line" />{org.region_city}</p>}
      {org.email && <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1 truncate"><i className="ri-mail-line" />{org.email}</p>}
      <div className="mt-auto pt-3 border-t border-stone-100 flex items-center justify-between">
        <span className="text-xs text-stone-400">{org.registration_date ? `Reg: ${org.registration_date.slice(0, 4)}` : ''}</span>
        <span className="text-xs font-semibold text-[#1a2e4a] flex items-center gap-1">
          {role === 'admin' ? 'View & Edit' : 'View'} <i className="ri-arrow-right-line" />
        </span>
      </div>
    </div>
  );
}

function SortableOrgCard({ org, lang, role, onOpen }: { org: Organization; lang: Lang; role: string; onOpen: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: org.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className="relative cursor-pointer group" onClick={() => !isDragging && onOpen(org.id)}>
      {/* drag handle — visible on group hover */}
      <div
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 bg-white/80 hover:bg-white border border-stone-200 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
        title="Drag to reorder"
      >
        <i className="ri-draggable text-sm" />
      </div>
      <OrgCardContent org={org} lang={lang} role={role} isDragging={isDragging} />
    </div>
  );
}

/* ─── List Row ───────────────────────────────────────────────────────── */
function SortableOrgRow({ org, lang, role, onOpen }: { org: Organization; lang: Lang; role: string; onOpen: (id: string) => void }) {
  const name = lang === 'en' ? org.name_en : org.name_ro;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: org.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 px-4 py-3 bg-white border rounded-xl cursor-pointer hover:border-[#1a2e4a]/30 transition-all group ${isDragging ? 'opacity-50 border-[#1a2e4a]/40' : 'border-stone-200'}`}
      onClick={() => !isDragging && onOpen(org.id)}
    >
      {/* drag handle */}
      <div
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-grab active:cursor-grabbing shrink-0 rounded"
        title="Drag to reorder"
      >
        <i className="ri-draggable text-base" />
      </div>
      {/* logo */}
      <div className="w-8 h-8 flex items-center justify-center bg-stone-50 rounded-lg border border-stone-100 shrink-0">
        {org.logo_url ? <img src={org.logo_url} alt="" className="w-6 h-6 object-contain" /> : <i className="ri-building-line text-[#1a2e4a] text-sm" />}
      </div>
      {/* name */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1a2e4a] truncate">{name}</p>
        {org.region_city && <p className="text-xs text-stone-400 truncate">{org.region_city}</p>}
      </div>
      {/* email */}
      <div className="hidden md:block w-48 truncate text-xs text-stone-400">{org.email ?? '—'}</div>
      {/* idno */}
      <div className="hidden lg:block w-32 text-xs text-stone-500 font-mono">{org.idno ?? '—'}</div>
      {/* reg date */}
      <div className="hidden sm:block w-20 text-xs text-stone-400 text-center">{org.registration_date ? org.registration_date.slice(0, 4) : '—'}</div>
      {/* status */}
      <div className="w-18 flex justify-end">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${org.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'}`}>
          {org.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      </div>
      {/* arrow */}
      <div className="w-6 h-6 flex items-center justify-center shrink-0">
        <i className="ri-arrow-right-line text-stone-300 group-hover:text-[#1a2e4a] transition-colors text-sm" />
      </div>
    </div>
  );
}

/* ─── Add Org Modal ──────────────────────────────────────────────────── */
function AddOrgModal({ onClose, onCreated, lang }: { onClose: () => void; onCreated: () => void; lang: Lang }) {
  const [form, setForm] = useState({ name_en: '', name_ro: '', email: '', region_city: '', legal_address: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.name_en.trim() || !form.name_ro.trim()) { setErr('Both EN and RO names are required.'); return; }
    setSaving(true);
    const { error } = await supabase.from('organizations').insert([{ ...form, status: 'active' }]);
    if (error) { setErr(error.message); setSaving(false); return; }
    onCreated();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-[#1a2e4a]">Add New Organization</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-pointer"><i className="ri-close-line text-lg" /></button>
        </div>
        <div className="space-y-3">
          {[
            { key: 'name_en', label: 'Name (English) *' },
            { key: 'name_ro', label: 'Name (Romanian) *' },
            { key: 'email', label: 'Email' },
            { key: 'region_city', label: 'Region / City' },
            { key: 'legal_address', label: 'Legal Address' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-stone-500 mb-1">{f.label}</label>
              <input
                value={(form as Record<string, string>)[f.key]}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
              />
            </div>
          ))}
          {err && <p className="text-xs text-red-500">{err}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 border border-stone-200 text-stone-600 text-sm font-medium rounded-lg py-2 hover:bg-stone-50 cursor-pointer whitespace-nowrap">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#1a2e4a] text-white text-sm font-semibold rounded-lg py-2 hover:bg-[#253f62] disabled:opacity-60 cursor-pointer whitespace-nowrap">
            {saving ? 'Saving...' : 'Create Organization'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sort helpers ───────────────────────────────────────────────────── */
const CUSTOM_ORDER_KEY = 'portal_orgs_order';

function loadCustomOrder(): string[] {
  try { return JSON.parse(localStorage.getItem(CUSTOM_ORDER_KEY) ?? '[]'); } catch { return []; }
}

function saveCustomOrder(ids: string[]) {
  localStorage.setItem(CUSTOM_ORDER_KEY, JSON.stringify(ids));
}

function applySort(orgs: Organization[], field: SortField, dir: SortDir, customIds: string[], lang: Lang): Organization[] {
  if (field === 'custom') {
    const idx = new Map(customIds.map((id, i) => [id, i]));
    return [...orgs].sort((a, b) => {
      const ia = idx.has(a.id) ? idx.get(a.id)! : 9999;
      const ib = idx.has(b.id) ? idx.get(b.id)! : 9999;
      return ia - ib;
    });
  }
  return [...orgs].sort((a, b) => {
    let va = '';
    let vb = '';
    if (field === 'name') {
      va = lang === 'en' ? a.name_en : a.name_ro;
      vb = lang === 'en' ? b.name_en : b.name_ro;
    } else if (field === 'region') {
      va = a.region_city ?? '';
      vb = b.region_city ?? '';
    } else if (field === 'status') {
      va = a.status;
      vb = b.status;
    } else if (field === 'date') {
      va = a.created_at;
      vb = b.created_at;
    }
    const cmp = va.localeCompare(vb);
    return dir === 'asc' ? cmp : -cmp;
  });
}

/* ─── Main Page ──────────────────────────────────────────────────────── */
export default function PartnerPortalPage() {
  const navigate = useNavigate();
  const { profile, loading, logout } = usePartnerAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lang, setLang] = useState<Lang>('en');
  const [showAdd, setShowAdd] = useState(false);

  // View & sort state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState<SortField>('custom');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [customIds, setCustomIds] = useState<string[]>(loadCustomOrder);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /* Redirect logic */
  useEffect(() => {
    if (!loading && !profile) navigate('/partner-access', { replace: true });
    if (!loading && profile?.role === 'partner' && profile.organization_id) {
      navigate(`/partner-portal/org/${profile.organization_id}`, { replace: true });
    }
    if (!loading && profile?.role === 'partner' && !profile.organization_id) {
      setOrgsLoading(false);
    }
  }, [profile, loading, navigate]);

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    const { data } = await supabase.from('organizations').select('*').order('name_en');
    const loaded = data ?? [];
    setOrgs(loaded);
    // Sync custom order: add new orgs at end, remove deleted
    setCustomIds(prev => {
      const existingIds = new Set(loaded.map(o => o.id));
      const kept = prev.filter(id => existingIds.has(id));
      const newIds = loaded.map(o => o.id).filter(id => !kept.includes(id));
      const merged = [...kept, ...newIds];
      saveCustomOrder(merged);
      return merged;
    });
    setOrgsLoading(false);
  }, []);

  useEffect(() => { if (profile && profile.role !== 'partner') loadOrgs(); }, [profile, loadOrgs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const matched = orgs.filter(o =>
      o.name_en.toLowerCase().includes(q) ||
      o.name_ro.toLowerCase().includes(q) ||
      (o.region_city ?? '').toLowerCase().includes(q) ||
      (o.idno ?? '').toLowerCase().includes(q)
    );
    return applySort(matched, sortField, sortDir, customIds, lang);
  }, [orgs, search, sortField, sortDir, customIds, lang]);

  const sortedIds = filtered.map(o => o.id);
  const activeOrg = activeId ? orgs.find(o => o.id === activeId) : null;

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Reorder within the full customIds list
    setCustomIds(prev => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      saveCustomOrder(next);
      return next;
    });
    setSortField('custom');
  }

  function toggleSort(field: SortField) {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  const handleLogout = async () => { await logout(); navigate('/partner-access', { replace: true }); };

  /* Loading screens */
  if (loading || orgsLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="w-8 h-8 border-2 border-[#1a2e4a] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profile) return null;

  /* Partner without assigned org */
  if (profile.role === 'partner' && !profile.organization_id) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <PortalHeader role={profile.role} fullName={profile.full_name} lang={lang} setLang={setLang} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white border border-stone-200 rounded-xl p-10 text-center max-w-md w-full">
            <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-building-line text-amber-500 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-[#1a2e4a] mb-2">No Organization Assigned</h2>
            <p className="text-sm text-stone-500 leading-relaxed mb-5">
              Your account has been approved, but no organization has been linked to your profile yet.
              Please contact the administrator to have your organization assigned.
            </p>
            <button onClick={handleLogout} className="text-sm text-stone-400 hover:text-stone-600 transition-colors cursor-pointer underline underline-offset-2">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* Sort indicator helper */
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <i className="ri-arrow-up-down-line text-stone-300 text-xs" />;
    return sortDir === 'asc'
      ? <i className="ri-arrow-up-line text-[#1a2e4a] text-xs" />
      : <i className="ri-arrow-down-line text-[#1a2e4a] text-xs" />;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <PortalHeader role={profile.role} fullName={profile.full_name} lang={lang} setLang={setLang} onLogout={handleLogout} />

      <div className="flex-1 px-6 md:px-10 py-8 max-w-7xl mx-auto w-full">

        {/* ── Toolbar ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-[#1a2e4a]">NGO Partner Catalog</h1>
            <p className="text-sm text-stone-500 mt-0.5">{filtered.length} organization{filtered.length !== 1 ? 's' : ''} found</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search organizations..."
                className="pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20 bg-white w-56"
              />
            </div>

            {/* Sort menu */}
            <div className="flex items-center bg-white border border-stone-200 rounded-lg overflow-hidden text-xs">
              {([
                { field: 'name' as SortField, label: 'Name' },
                { field: 'region' as SortField, label: 'Region' },
                { field: 'status' as SortField, label: 'Status' },
                { field: 'date' as SortField, label: 'Date' },
                { field: 'custom' as SortField, label: 'Custom' },
              ] as const).map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => toggleSort(field)}
                  className={`flex items-center gap-1 px-2.5 py-2 border-r border-stone-100 last:border-0 transition-colors cursor-pointer whitespace-nowrap ${sortField === field ? 'bg-[#1a2e4a]/5 text-[#1a2e4a] font-semibold' : 'text-stone-500 hover:bg-stone-50'}`}
                >
                  {label} <SortIcon field={field} />
                </button>
              ))}
            </div>

            {/* View toggle */}
            <div className="flex items-center bg-white border border-stone-200 rounded-lg p-0.5">
              <button onClick={() => setViewMode('grid')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-[#1a2e4a] text-white' : 'text-stone-400 hover:text-stone-700'}`}>
                <i className="ri-grid-fill text-sm" />
              </button>
              <button onClick={() => setViewMode('list')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-[#1a2e4a] text-white' : 'text-stone-400 hover:text-stone-700'}`}>
                <i className="ri-list-check-2 text-sm" />
              </button>
            </div>

            {/* Add org */}
            {profile.role === 'admin' && (
              <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#1a2e4a] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#253f62] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-line" />Add Organization
              </button>
            )}
          </div>
        </div>

        {/* ── Drag hint ── */}
        {sortField === 'custom' && (
          <p className="text-xs text-stone-400 mb-4 flex items-center gap-1.5">
            <i className="ri-draggable" />
            Drag cards to set a custom order. Order is saved automatically.
          </p>
        )}

        {/* ── Empty state ── */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-xl mx-auto mb-3">
              <i className="ri-building-line text-stone-400 text-2xl" />
            </div>
            <p className="text-stone-500 text-sm">No organizations found</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedIds} strategy={rectSortingStrategy}>

              {/* ── Grid View ── */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filtered.map(org => (
                    <SortableOrgCard key={org.id} org={org} lang={lang} role={profile.role} onOpen={id => navigate(`/partner-portal/org/${id}`)} />
                  ))}
                </div>
              )}

              {/* ── List View ── */}
              {viewMode === 'list' && (
                <div className="space-y-1.5">
                  {/* List header */}
                  <div className="flex items-center gap-4 px-4 py-2 text-xs font-semibold text-stone-400 uppercase tracking-wide">
                    <div className="w-5 shrink-0" />
                    <div className="w-8 shrink-0" />
                    <div className="flex-1">
                      <button onClick={() => toggleSort('name')} className="flex items-center gap-1 hover:text-stone-600 cursor-pointer whitespace-nowrap">
                        Organization <SortIcon field="name" />
                      </button>
                    </div>
                    <div className="hidden md:block w-48">
                      <button onClick={() => toggleSort('region')} className="flex items-center gap-1 hover:text-stone-600 cursor-pointer whitespace-nowrap">
                        Email
                      </button>
                    </div>
                    <div className="hidden lg:block w-32 text-xs">IDNO</div>
                    <div className="hidden sm:block w-20 text-center">
                      <button onClick={() => toggleSort('date')} className="flex items-center gap-1 hover:text-stone-600 cursor-pointer whitespace-nowrap">
                        Year <SortIcon field="date" />
                      </button>
                    </div>
                    <div className="w-18 text-right">
                      <button onClick={() => toggleSort('status')} className="flex items-center gap-1 hover:text-stone-600 cursor-pointer whitespace-nowrap">
                        Status <SortIcon field="status" />
                      </button>
                    </div>
                    <div className="w-6 shrink-0" />
                  </div>

                  {filtered.map(org => (
                    <SortableOrgRow key={org.id} org={org} lang={lang} role={profile.role} onOpen={id => navigate(`/partner-portal/org/${id}`)} />
                  ))}
                </div>
              )}

            </SortableContext>

            {/* Drag overlay */}
            <DragOverlay>
              {activeOrg && viewMode === 'grid' && (
                <div className="w-56 opacity-90 rotate-1">
                  <OrgCardContent org={activeOrg} lang={lang} role={profile.role} />
                </div>
              )}
              {activeOrg && viewMode === 'list' && (
                <div className="flex items-center gap-4 px-4 py-3 bg-white border border-[#1a2e4a]/30 rounded-xl shadow-lg text-sm font-semibold text-[#1a2e4a]">
                  <i className="ri-draggable text-stone-400" />
                  {lang === 'en' ? activeOrg.name_en : activeOrg.name_ro}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {showAdd && <AddOrgModal lang={lang} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); loadOrgs(); }} />}
    </div>
  );
}
